import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ArrowRight, Bell, Trash2, Search, Filter, CheckCircle2, Circle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface UnifiedNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  source: 'admin' | 'landlord' | 'system';
  is_read: boolean;
  created_at: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  recipient_type?: string;
}

const NotificationsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser } = useCurrentUser();
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<string>('');
  const [profileId, setProfileId] = useState<string>('');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const userTypeParam = searchParams.get('type') || 'landlord';

  useEffect(() => {
    initializeUser();
  }, []);

  const initializeUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const type = user.user_metadata?.user_type || 'tenant';
      setUserType(type);

      if (type === 'landlord') {
        const { data: landlordData } = await supabase
          .from('landlords')
          .select('id')
          .eq('user_id', user.id)
          .single();
        if (landlordData) {
          setProfileId(landlordData.id);
          loadNotifications(landlordData.id, 'landlord');
        }
      } else if (type === 'tenant') {
        const { data: tenantData } = await supabase
          .from('tenants')
          .select('id')
          .eq('user_id', user.id)
          .single();
        if (tenantData) {
          setProfileId(tenantData.id);
          loadNotifications(tenantData.id, 'tenant');
        }
      }
    } catch (error) {
      console.error('Error initializing user:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async (userId: string, type: string) => {
    try {
      const notifications: UnifiedNotification[] = [];

      // Fetch admin notifications
      const { data: adminData } = await supabase
        .from('admin_notifications')
        .select('*')
        .or(`recipient_type.eq.all,and(recipient_type.eq.${type},recipient_id.eq.${userId}),and(recipient_type.eq.${type},recipient_id.is.null)`)
        .order('created_at', { ascending: false });

      if (adminData) {
        const mappedAdminNotifications = adminData.map(notification => ({
          id: notification.id,
          title: notification.title,
          message: notification.message,
          type: notification.notification_type,
          source: 'admin' as const,
          is_read: notification.is_read || false,
          created_at: notification.created_at,
          priority: getPriorityFromType(notification.notification_type),
          category: getCategoryFromType(notification.notification_type),
          recipient_type: notification.recipient_type
        }));
        notifications.push(...mappedAdminNotifications);
      }

      // Fetch landlord notifications if user is landlord
      if (type === 'landlord') {
        const { data: landlordData } = await supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false });

        if (landlordData) {
          const mappedLandlordNotifications = landlordData.map(notification => ({
            id: `landlord_${notification.id}`,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            source: 'landlord' as const,
            is_read: notification.is_read || false,
            created_at: notification.created_at,
            priority: getPriorityFromType(notification.type),
            category: getCategoryFromType(notification.type)
          }));
          notifications.push(...mappedLandlordNotifications);
        }
      }

      // Sort by date and priority
      const sortedNotifications = notifications.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        
        if (dateA === dateB) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return dateB - dateA;
      });

      setNotifications(sortedNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({
        title: "خطأ في تحميل الإشعارات",
        description: "حدث خطأ أثناء تحميل الإشعارات",
        variant: "destructive"
      });
    }
  };

  const getPriorityFromType = (type: string): 'low' | 'medium' | 'high' => {
    switch (type) {
      case 'rental_request':
      case 'subscription_expired':
      case 'account_activation':
        return 'high';
      case 'subscription_reminder':
      case 'account_status':
        return 'medium';
      default:
        return 'low';
    }
  };

  const getCategoryFromType = (type: string): string => {
    switch (type) {
      case 'rental_request':
        return 'طلبات الإيجار';
      case 'subscription_reminder':
      case 'subscription_expired':
        return 'الاشتراكات';
      case 'account_activation':
      case 'account_status':
        return 'الحساب';
      case 'admin_message':
        return 'رسائل إدارية';
      default:
        return 'عام';
    }
  };

  const markAsRead = async (notificationId: string, source: string) => {
    try {
      if (source === 'admin') {
        await supabase
          .from('admin_notifications')
          .update({ is_read: true })
          .eq('id', notificationId);
      } else if (source === 'landlord' && notificationId.startsWith('landlord_')) {
        const originalId = notificationId.replace('landlord_', '');
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', originalId);
      }

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );

      toast({
        title: "تم وضع علامة كمقروء",
        description: "تم تحديث حالة الإشعار"
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string, source: string) => {
    try {
      if (source === 'admin') {
        await supabase
          .from('admin_notifications')
          .delete()
          .eq('id', notificationId);
      } else if (source === 'landlord' && notificationId.startsWith('landlord_')) {
        const originalId = notificationId.replace('landlord_', '');
        await supabase
          .from('notifications')
          .delete()
          .eq('id', originalId);
      }

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast({
        title: "تم الحذف",
        description: "تم حذف الإشعار بنجاح"
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast({
        title: "تم إخفاء الإشعار",
        description: "تم إخفاء الإشعار من قائمتك",
        variant: "default"
      });
    }
  };

  const goBack = () => {
    if (userType === 'landlord') {
      navigate('/owner-dashboard');
    } else {
      navigate('/user-dashboard');
    }
  };

  const getPriorityColor = (priority: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return `منذ ${diffInMinutes} دقيقة`;
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else {
      return new Intl.DateTimeFormat('ar', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesTab = activeTab === 'all' || (activeTab === 'unread' && !notification.is_read);
    const matchesSearch = notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || notification.category === selectedCategory;
    
    return matchesTab && matchesSearch && matchesCategory;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const categories = ['all', ...Array.from(new Set(notifications.map(n => n.category)))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل الإشعارات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 font-['Tajawal',sans-serif] rtl">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="outline"
              onClick={goBack}
              className="flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              العودة
            </Button>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2 justify-center">
                <Bell className="h-8 w-8 text-primary" />
                مركز الإشعارات
              </h1>
              <p className="text-gray-600 mt-1">جميع الرسائل والتنبيهات الواردة إليك</p>
            </div>
            <div className="w-24"> {/* Spacer for symmetry */}</div>
          </div>

          {/* Search and Filter Section */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="البحث في الإشعارات..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="all">جميع الفئات</option>
                    {categories.filter(cat => cat !== 'all').map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">إجمالي الإشعارات</p>
                    <p className="text-2xl font-bold">{notifications.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Circle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">غير مقروءة</p>
                    <p className="text-2xl font-bold text-red-600">{unreadCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">مقروءة</p>
                    <p className="text-2xl font-bold text-green-600">{notifications.length - unreadCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notifications Tabs */}
          <Card>
            <CardHeader>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="all">
                    جميع الإشعارات ({notifications.length})
                  </TabsTrigger>
                  <TabsTrigger value="unread" className="relative">
                    غير مقروءة 
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {unreadCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <div className="mt-6">
                  <TabsContent value="all" className="space-y-4">
                    <NotificationsList 
                      notifications={filteredNotifications}
                      onMarkAsRead={markAsRead}
                      onDelete={deleteNotification}
                      formatDate={formatDate}
                      getPriorityColor={getPriorityColor}
                    />
                  </TabsContent>

                  <TabsContent value="unread" className="space-y-4">
                    <NotificationsList 
                      notifications={filteredNotifications}
                      onMarkAsRead={markAsRead}
                      onDelete={deleteNotification}
                      formatDate={formatDate}
                      getPriorityColor={getPriorityColor}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
};

interface NotificationsListProps {
  notifications: UnifiedNotification[];
  onMarkAsRead: (id: string, source: string) => void;
  onDelete: (id: string, source: string) => void;
  formatDate: (date: string) => string;
  getPriorityColor: (priority: string) => "default" | "destructive" | "secondary" | "outline";
}

const NotificationsList = ({ 
  notifications, 
  onMarkAsRead, 
  onDelete, 
  formatDate, 
  getPriorityColor 
}: NotificationsListProps) => {
  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
          <Bell className="h-8 w-8 text-muted-foreground opacity-50" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد إشعارات</h3>
        <p className="text-gray-500">لم تتلق أي إشعارات حتى الآن</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {notifications.map((notification) => (
        <Card 
          key={notification.id} 
          className={`transition-all hover:shadow-lg ${
            !notification.is_read ? 'border-l-4 border-l-primary bg-primary/5' : ''
          }`}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  )}
                  <h3 className="text-lg font-semibold">{notification.title}</h3>
                  <Badge variant={getPriorityColor(notification.priority)}>
                    {notification.priority === 'high' && 'عالي'}
                    {notification.priority === 'medium' && 'متوسط'}
                    {notification.priority === 'low' && 'منخفض'}
                  </Badge>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-4">{notification.message}</p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <Badge variant="outline">
                    {notification.category}
                  </Badge>
                  <span>{formatDate(notification.created_at)}</span>
                  <Badge variant="secondary">
                    {notification.source === 'admin' ? 'إشعار إداري' : 'إشعار نشاط'}
                  </Badge>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                {!notification.is_read && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onMarkAsRead(notification.id, notification.source)}
                    className="text-xs"
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    وضع علامة كمقروء
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(notification.id, notification.source)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  حذف
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default NotificationsPage;